import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Eye, Trash2, Plus, RefreshCw, Star } from 'lucide-react';
import { useCertificados, useAtualizarCertificadoAtivo } from '@/services/Configuracoes';
import type { CertificadoApi } from '@/services/Configuracoes';
import { useState } from 'react';
import { AdicionarCertificadoDialog } from './AdicionarCertificadoDialog';

const situacaoConfig: Record<string, string> = {
  Válido: 'bg-green-500 text-white hover:bg-green-500',
  Vencido: 'bg-red-500 text-white hover:bg-red-500',
  'A vencer': 'bg-yellow-500 text-white hover:bg-yellow-500',
};

function AtivoCell({ cert }: { cert: CertificadoApi }) {
  const { mutate, isPending } = useAtualizarCertificadoAtivo();

  const switchEl = (
    <Switch
      checked={cert.Ativo}
      disabled={cert.AtivoForcado || isPending}
      onCheckedChange={checked => mutate({ CNPJ: cert.CNPJ, Ativo: checked })}
    />
  );

  if (!cert.AtivoForcado) return switchEl;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{switchEl}</span>
      </TooltipTrigger>
      <TooltipContent>Sempre ativo para empresas NAGUMO</TooltipContent>
    </Tooltip>
  );
}

export function GestaoCertificadosTab() {
  const { data: response, isLoading } = useCertificados();
  const certificados = response?.data ?? [];
  const [adicionarOpen, setAdicionarOpen] = useState(false);

  return (
    <TabsContent value="certificados" className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-base">Certificados</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Atualizar Certificado Padrão nas Filiais
            </Button>
            <Button size="sm" className="gap-2" onClick={() => setAdicionarOpen(true)}>
              <Plus className="h-4 w-4" />
              Adicionar Certificado
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-16">Tipo</TableHead>
                  <TableHead>Emissor</TableHead>
                  <TableHead className="w-44">Validade</TableHead>
                  <TableHead className="w-28">Situação</TableHead>
                  <TableHead className="w-20 text-center">Padrão</TableHead>
                  <TableHead className="w-24">Categoria</TableHead>
                  <TableHead>Cadastrado por</TableHead>
                  <TableHead className="w-20 text-center">Ativo</TableHead>
                  <TableHead className="w-32 text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      Nenhum certificado encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  certificados.map(cert => (
                    <TableRow key={cert.ID}>
                      <TableCell className="text-muted-foreground">{cert.ID}</TableCell>
                      <TableCell className="font-medium">{cert.Tipo}</TableCell>
                      <TableCell>{cert.Emissor}</TableCell>
                      <TableCell className="text-sm">{cert.Validade}</TableCell>
                      <TableCell>
                        <Badge
                          className={situacaoConfig[cert.Situacao] ?? 'bg-gray-400 text-white'}
                        >
                          {cert.Situacao}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {cert.Padrao ? (
                          <div className="flex justify-center">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          </div>
                        ) : (
                          <Button variant="link" size="sm" className="h-auto p-0 text-xs">
                            Definir
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{cert.Categoria}</TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-pre-line">
                        {cert.CadastradoPor}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center">
                          <AtivoCell cert={cert} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs">
                            <Eye className="h-3.5 w-3.5" />
                            Visualizar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-xs text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remover
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AdicionarCertificadoDialog open={adicionarOpen} onOpenChange={setAdicionarOpen} />
    </TabsContent>
  );
}
